import { identityKeys, type ManagedUser, sessionKeys, type UserSession } from '@taler/api-client'
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query'
import { computed, type ComputedRef, ref } from 'vue'
import { useApi } from '@/app/api'
import { retrying } from '@/app/retrying'

export interface UseIdentityAdminOptions {
  readonly subject: () => string | undefined
}

export interface UseIdentityAdmin {
  readonly sessions: ComputedRef<readonly UserSession[]>
  readonly isLoadingSessions: ComputedRef<boolean>
  readonly sessionsError: ComputedRef<Error | null>
  readonly retrying: ComputedRef<boolean>
  readonly endingSessionId: ComputedRef<string | undefined>
  readonly endSessionError: ComputedRef<Error | null>
  endSession(sessionId: string): void
  /** The last suspend/restore result for the looked-up subject - there is no get-user
   * operation, so status is unknown until one of those two actions runs. */
  readonly status: ComputedRef<ManagedUser | undefined>
  readonly isSuspending: ComputedRef<boolean>
  readonly suspendError: ComputedRef<Error | null>
  suspend(): void
  readonly isRestoring: ComputedRef<boolean>
  readonly restoreError: ComputedRef<Error | null>
  restore(): void
  retry(): void
}

export function useIdentityAdmin(options: UseIdentityAdminOptions): UseIdentityAdmin {
  const api = useApi()
  const queryClient = useQueryClient()
  const status = ref<ManagedUser>()

  const sessionsQuery = useQuery({
    queryKey: computed(() => identityKeys.sessions(options.subject() ?? '')),
    queryFn: ({ signal }) => api.identity.listSessions(options.subject()!, signal),
    enabled: computed(() => options.subject() !== undefined),
    retry: false,
  })

  function invalidateSessions(): void {
    const subject = options.subject()
    if (subject === undefined) return
    void queryClient.invalidateQueries({ queryKey: identityKeys.sessions(subject) })
    // Ending or suspending may have just ended the caller's own session - the next read of
    // the principal surfaces that through the app's ordinary sign-in-required handling.
    void queryClient.invalidateQueries({ queryKey: sessionKeys.principal() })
  }

  const endSessionMutation = useMutation({
    mutationFn: (sessionId: string) => api.identity.endSession(options.subject()!, sessionId),
    onSuccess: invalidateSessions,
  })

  const suspendMutation = useMutation({
    mutationFn: () => api.identity.suspend(options.subject()!),
    onSuccess: (result) => {
      status.value = result
      invalidateSessions()
    },
  })

  const restoreMutation = useMutation({
    mutationFn: () => api.identity.restore(options.subject()!),
    onSuccess: (result) => {
      status.value = result
    },
  })

  return {
    sessions: computed(() => sessionsQuery.data.value ?? []),
    isLoadingSessions: computed(() => sessionsQuery.isPending.value && options.subject() !== undefined),
    sessionsError: computed(() => sessionsQuery.error.value),
    retrying: retrying(sessionsQuery),
    endingSessionId: computed(() =>
      endSessionMutation.isPending.value
        ? (endSessionMutation.variables.value as string | undefined)
        : undefined,
    ),
    endSessionError: computed(() => endSessionMutation.error.value),
    endSession: (sessionId) => endSessionMutation.mutate(sessionId),
    status: computed(() => status.value),
    isSuspending: computed(() => suspendMutation.isPending.value),
    suspendError: computed(() => suspendMutation.error.value),
    suspend: () => suspendMutation.mutate(),
    isRestoring: computed(() => restoreMutation.isPending.value),
    restoreError: computed(() => restoreMutation.error.value),
    restore: () => restoreMutation.mutate(),
    retry: () => void sessionsQuery.refetch(),
  }
}
